import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { type TaskView } from '@apm/contracts';
import { displayDate } from '../../../lib/date';

/** `2026-09-11T07:23:00Z` → `11/09/2026 14:23`. `null` → dấu gạch. */
function moment(value: string | null, locale: string): string | null {
  if (value === null) return null;
  return new Date(value).toLocaleString(locale === 'en' ? 'en-GB' : 'vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Kế hoạch **cạnh** thực tế — APM-TASK-j.
 *
 * ## Vì sao hai cột, không phải hai khối
 *
 * Cả giá trị của dữ liệu này nằm ở chỗ **so sánh**: ước 8 giờ mà làm 20 giờ là
 * thông tin, còn "làm 20 giờ" một mình thì không nói được gì. Hai khối rời nhau
 * bắt người đọc tự ghép, và ở một ngăn kéo hẹp thì khối thứ hai còn nằm ngoài
 * tầm nhìn.
 *
 * ## Vì sao "thời gian trôi qua" hiện riêng, không thay `actualHours`
 *
 * Quãng từ lúc bắt đầu tới lúc xong gồm cả đêm, cuối tuần và những ngày việc
 * nằm im chờ review. Một việc mở ba ngày mà chỉ chạm vào hai tiếng thì hai con
 * số ấy lệch nhau hai chục lần. Lấy quãng trôi làm công sức là cách nhanh nhất
 * để mọi ước lượng sau này sai theo — nên nó hiện ra, nhưng ở một dòng khác và
 * có tên khác.
 *
 * ## Cột Baseline — người dùng yêu cầu 2026-09-23
 *
 * Baseline là **bản chụp kế hoạch lúc chốt** (`baseline_start_date`/
 * `baseline_due_date`), còn cột "Hiện tại" là deadline đang áp và sửa được bất
 * cứ lúc nào. Dải "việc đang chậm deadline" đếm theo cột Hiện tại; baseline chỉ
 * trả lời "so với lời hứa ban đầu thì lệch bao nhiêu" — nên nó đứng ngay cạnh
 * để mắt so được, và hạn hiện tại đổi màu khi đã bị dời khỏi baseline.
 *
 * Chưa chốt baseline (hoặc việc sinh ra sau lần chốt) thì cột ghi "—" — không
 * bịa mốc từ deadline hiện tại.
 *
 * ## Cột Thực tế không sửa được — APM-TDRW-d
 *
 * Hai mốc do luồng trạng thái đặt (vào cột "đang làm" lần đầu, vào cột "xong"),
 * số giờ là tổng giờ bấm đồng hồ đã đóng (APM-ACTH). Từng sửa tay được giờ (PM,
 * quản lý của người làm); người dùng bỏ quyền ấy 2026-11-02 — "không cho sửa số
 * giờ làm việc thực tế qua bấm giờ" — nên API cũng từ chối.
 *
 * Bảng không kèm câu giải thích nào ngoài quãng trôi qua: "—" ở cột Thực tế đã
 * nói "chưa có", nguồn của số giờ nằm ở tooltip (người dùng chê in thừa, 2026-11-02).
 */
export function TaskActualTime({ task }: { readonly task: TaskView }): ReactNode {
  const { t, i18n } = useTranslation();

  const started = moment(task.actualStartAt, i18n.language);
  const ended = moment(task.actualEndAt, i18n.language);

  /**
   * Thời gian trôi qua giữa hai mốc, làm tròn tới giờ.
   *
   * Chỉ tính khi có **cả hai**: một việc đang làm dở thì quãng ấy còn đang dài
   * ra, và một con số đứng yên trên màn hình sẽ nói dối ngay giây sau.
   */
  const elapsedHours =
    task.actualStartAt !== null && task.actualEndAt !== null
      ? Math.max(
          0,
          Math.round(
            (new Date(task.actualEndAt).getTime() - new Date(task.actualStartAt).getTime()) / 36e5,
          ),
        )
      : null;

  /** Deadline hiện tại đã bị dời khỏi baseline — tô màu để mắt thấy ngay. */
  const dueMoved =
    task.baselineDueDate != null && task.dueDate !== null && task.dueDate !== task.baselineDueDate;

  const over =
    task.estimateHours !== null &&
    task.actualHours !== null &&
    task.actualHours > task.estimateHours;

  // Tiêu đề và nút gập là của drawer (`CollapsibleSection`) — người dùng yêu cầu 2026-11-02.
  return (
    <div>
      <dl className="grid grid-cols-[auto_1fr_1fr_1fr] items-baseline gap-x-3 gap-y-1.5 text-xs">
        <dt className="text-muted-foreground" />
        <dd className="font-medium text-muted-foreground" title={t('actualTime.baselineHelp')}>
          {t('actualTime.baseline')}
        </dd>
        <dd className="font-medium text-muted-foreground">{t('actualTime.planned')}</dd>
        <dd className="font-medium text-muted-foreground">{t('actualTime.actual')}</dd>

        <dt className="text-muted-foreground">{t('actualTime.start')}</dt>
        <dd className="tabular-nums text-muted-foreground">
          {displayDate(task.baselineStartDate ?? null)}
        </dd>
        <dd className="tabular-nums">{displayDate(task.startDate)}</dd>
        <dd className="tabular-nums">
          {started ?? '—'}
          {/* Mốc người dùng tự khai (APM-TMIN-b), không phải hệ thống đo. */}
          {started && task.actualStartDeclared ? (
            <span
              className="ml-1.5 whitespace-nowrap rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
              title={t('taskMinDuration.declaredHint')}
            >
              {t('taskMinDuration.declared')}
            </span>
          ) : null}
        </dd>

        <dt className="text-muted-foreground">{t('actualTime.end')}</dt>
        <dd className="tabular-nums text-muted-foreground">
          {displayDate(task.baselineDueDate ?? null)}
        </dd>
        <dd
          className={`tabular-nums ${dueMoved ? 'font-medium text-rag-amber' : ''}`}
          title={dueMoved ? t('actualTime.dueMoved') : undefined}
        >
          {displayDate(task.dueDate)}
        </dd>
        <dd className="tabular-nums">{ended ?? '—'}</dd>

        <dt className="text-muted-foreground">{t('actualTime.hours')}</dt>
        {/* Baseline chỉ chụp ngày, không chụp số giờ ước lượng. */}
        <dd className="text-muted-foreground">—</dd>
        <dd className="tabular-nums">
          {task.estimateHours === null
            ? '—'
            : t('actualTime.hoursValue', { count: task.estimateHours })}
        </dd>
        <dd
          className={`tabular-nums ${over ? 'font-medium text-rag-amber' : ''}`}
          title={t('actualTime.actualHoursHelp')}
          data-testid="actual-hours"
        >
          {task.actualHours === null
            ? '—'
            : t('actualTime.hoursValue', { count: task.actualHours })}
        </dd>
      </dl>

      {/* Quãng trôi qua đứng tách hẳn ở dưới, có tên riêng — xem docblock. */}
      {elapsedHours === null ? null : (
        <p className="mt-2 text-[11px] text-muted-foreground">
          {t('actualTime.elapsed', { count: elapsedHours })}
        </p>
      )}
    </div>
  );
}
